import {
  ALL_FORMATS,
  BlobSource,
  BufferTarget,
  Conversion,
  Input,
  Mp4OutputFormat,
  Output,
  QUALITY_HIGH,
  QUALITY_LOW,
  QUALITY_MEDIUM,
  WebMOutputFormat,
} from "mediabunny";

export type VideoContainer = "mp4" | "webm";
export type VideoQuality = "low" | "medium" | "high";

export interface ConvertVideoOptions {
  /** Output container. */
  format: VideoContainer;
  /** Keep only `[start, end]` (seconds). */
  trim?: { start: number; end: number };
  /** Downscale so height ≤ this many pixels (keeps aspect; never upscales). */
  maxHeight?: number;
  /** Re-encode video at this quality preset (for compression). */
  quality?: VideoQuality;
  /** Drop the audio track. */
  discardAudio?: boolean;
  /** Rotate clockwise by this many degrees. */
  rotate?: 90 | 180 | 270;
  onProgress?: (progress: number) => void;
}

export interface ConvertedVideo {
  data: ArrayBuffer;
  mimeType: string;
  extension: string;
}

const QUALITY = { low: QUALITY_LOW, medium: QUALITY_MEDIUM, high: QUALITY_HIGH };

/**
 * The one video workhorse, powered by mediabunny: convert container, trim,
 * downscale, compress (re-encode at a quality preset), drop audio, and/or
 * rotate — in any combination. Transmuxes (copies packets) when it can and only
 * transcodes (WebCodecs) when an option forces it. Browser-only.
 */
export async function convertVideo(
  file: Blob,
  options: ConvertVideoOptions
): Promise<ConvertedVideo> {
  const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
  const isWebm = options.format === "webm";
  const output = new Output({
    format: isWebm ? new WebMOutputFormat() : new Mp4OutputFormat(),
    target: new BufferTarget(),
  });

  const video: {
    height?: number;
    bitrate?: number | typeof QUALITY_HIGH;
    rotate?: 90 | 180 | 270;
  } = {};
  if (options.maxHeight) {
    const track = await input.getPrimaryVideoTrack();
    if (track && track.displayHeight > options.maxHeight) video.height = options.maxHeight;
  }
  if (options.quality) video.bitrate = QUALITY[options.quality];
  if (options.rotate) video.rotate = options.rotate;

  const conversion = await Conversion.init({
    input,
    output,
    ...(options.trim ? { trim: options.trim } : {}),
    ...(Object.keys(video).length > 0 ? { video } : {}),
    ...(options.discardAudio ? { audio: { discard: true } } : {}),
  });

  if (!conversion.isValid) {
    throw new Error("This video can't be processed in the browser (no convertible tracks).");
  }
  if (options.onProgress) conversion.onProgress = options.onProgress;

  try {
    await conversion.execute();
  } finally {
    input.dispose();
  }

  const data = output.target.buffer;
  if (!data) throw new Error("Processing produced no output.");
  return {
    data,
    mimeType: isWebm ? "video/webm" : "video/mp4",
    extension: isWebm ? "webm" : "mp4",
  };
}
